import React, { useCallback, useContext, useMemo } from "react";
import fp from "lodash/fp";
import { Col, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { Field, change } from "redux-form";

import { CustomInput, RequiredMarkContext } from "components";
import { MerchantSensitiveDataContext } from "components/contexts";
import SelectAsyncForBankName from "components/select/selectAsyncForBankName";
import SelectAsyncForBankRouting from "components/select/selectAsyncForBankRouting";
import { DebounceTimeout } from "globalConstants";
import { dropdownValuesService } from "services";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";
import {
	maxLength17,
	maxLength50,
	maxLength9,
	minLength6,
	normalizeAll,
	onlyAlphanumericAndSpecialCharacters,
	onlyDigits,
	required,
} from "validators/validation";

import styles from "./businessInfo.module.scss";

const BankInformation = ({
	banks,
	setBanks,
	formName,
	dropDownHeight,
	menuPlacement,
	routingExists,
	hasTitle = false,
	isDisabled = false,
	routingName = "routing",
	bankName = "bank",
	accountName = "account",
	accountNameName = "accountName",
	additionalLabel = "",
	isLoading = false,
}) => {
	const dispatch = useDispatch();

	const { hasBankChangePermission } = useContext(
		MerchantSensitiveDataContext,
	);

	const isRequired = useContext(RequiredMarkContext);

	const isReadonly = useMemo(() => {
		if (typeof hasBankChangePermission === "undefined") {
			return false;
		} else {
			return !hasBankChangePermission;
		}
	}, [hasBankChangePermission]);

	const PAGE_SIZE = 20;

	const loadBankRoutings = useCallback(
		async (search, prevOptions, { page }) => {
			const oldBanks = [...banks];
			try {
				if (search?.length < 3) {
					setBanks([]);
					return {
						options: [
							{
								value: undefined,
								label: undefined,
							},
						],
					};
				} else {
					const body = {
						pattern: search,
						page,
						pageSize: PAGE_SIZE,
					};

					const { items, count } =
						await dropdownValuesService.getBanks(body);

					const hasMore = prevOptions.length + items.length < count;
					const newBanks = fp.uniqBy("id", [...oldBanks, ...items]);

					if (fp.isEmpty(items)) {
						setBanks([]);
						return {
							options: [
								{
									value: search,
									label: search,
								},
							],
						};
					} else {
						setBanks(newBanks);
						return {
							options: newBanks.map(b => ({
								value: b.routing,
								label: b.routing,
							})),
							hasMore,
							additional: {
								page: page + 1,
							},
						};
					}
				}
			} catch {
				return { options: [] };
			}
		},
		[banks, setBanks],
	);

	const handleChangeBankName = bankRouting => {
		const bankWithRouting = fp.find(["routing", bankRouting], banks);
		if (fp.isEmpty(bankWithRouting)) {
			dispatch(change(formName, bankName, ""));
		}

		if (bankWithRouting) {
			dispatch(change(formName, bankName, bankWithRouting.bank));
		}
	};

	const handleSyncBankByRouting = routing => {
		if (!routing) return null;
		const bankByRouting = fp.find(["routing", routing], banks);

		return bankByRouting
			? { value: bankByRouting.routing, label: bankByRouting.routing }
			: { value: routing, label: routing };
	};

	const handleSyncBankByName = name => {
		if (!name) return null;
		const bankByName = fp.find(["bank", name], banks);

		return bankByName
			? { value: bankByName.bank, label: bankByName.bank }
			: { value: name, label: name };
	};

	return (
		<>
			{hasTitle && (
				<div className={`${styles.subHeader} mb-3`}>
					Where do we send your money?
				</div>
			)}
			<Row>
				<Col lg={5}>
					<Field
						component={SelectAsyncForBankRouting}
						label={`${additionalLabel}Routing`}
						cacheOptions={false}
						loadOptions={loadBankRoutings}
						onChangeCb={handleChangeBankName}
						findOption={handleSyncBankByRouting}
						debounceTimeout={DebounceTimeout}
						dropDownHeight={dropDownHeight}
						menuPlacement={menuPlacement}
						validate={
							isRequired && !isDisabled
								? [required, maxLength9]
								: [maxLength9]
						}
						name={routingName}
						normalize={onlyDigits}
						disabled={isDisabled}
						readOnly={isDisabled || isReadonly}
						blurLoading={isLoading}
					/>
				</Col>
				<Col lg={{ span: 5, offset: 2 }}>
					{routingExists ? (
						<Field
							component={SelectAsyncForBankName}
							label={`${additionalLabel}Bank`}
							cacheOptions
							debounceTimeout={DebounceTimeout}
							findOption={handleSyncBankByName}
							dropDownHeight={dropDownHeight}
							menuPlacement={menuPlacement}
							name={bankName}
							readOnly
							disabled={isDisabled}
							blurLoading={isLoading}
						/>
					) : (
						<Field
							component={CustomInput}
							label={`${additionalLabel}Bank`}
							name={bankName}
							hideRequiredMark
							validate={[maxLength50]}
							normalizeOnBlur={trimNormalizer}
							normalize={normalizeAll([
								onlyAlphanumericAndSpecialCharacters,
								trimMidSentenceNormalizer,
							])}
							disabled={isDisabled}
							readOnly={isDisabled || isReadonly}
							blurLoading={isLoading}
						/>
					)}
				</Col>
			</Row>
			<Row>
				<Col lg={5}>
					<Field
						component={CustomInput}
						label={`${additionalLabel}Account`}
						validate={
							isRequired && !isDisabled
								? [required, minLength6, maxLength17]
								: [minLength6, maxLength17]
						}
						normalize={onlyDigits}
						name={accountName}
						disabled={isDisabled}
						readOnly={isDisabled || isReadonly}
						blurLoading={isLoading}
					/>
				</Col>
				<Col lg={{ span: 5, offset: 2 }}>
					<Field
						component={CustomInput}
						label={`${additionalLabel}Account Name`}
						name={accountNameName}
						hideRequiredMark
						normalizeOnBlur={trimNormalizer}
						normalize={normalizeAll([
							onlyAlphanumericAndSpecialCharacters,
							trimMidSentenceNormalizer,
						])}
						disabled={isDisabled}
						readOnly={isDisabled || isReadonly}
						blurLoading={isLoading}
					/>
				</Col>
			</Row>
		</>
	);
};

export default BankInformation;
